/*
-------------------------------------------------------------------------------------
Authors         : Alinsonorin, John Myl B., Awi, Joseph Kahl L., Gozon, Daniel Allan
Date Created    : April 30, 2025
Folder          : src/components/homepage
File            : Homepage.tsx
Description     : 
    This component allows the user to access the homepage of the web app.
Copyright © 2025. All rights reserved.
-------------------------------------------------------------------------------------
*/



// Import React Systems
import { useRef, useState, useEffect, useLayoutEffect } from 'react';
import { useNavigate, useLocation, Link } from "react-router-dom";

// Import Atoms
import SplashScreen from "../../../components/UI/atoms/splash/SplashScreen"

// Import Firebase Systems
import app from "../../../backend/firebaseConfig";
import {  
 getAuth, 
  signInWithEmailAndPassword,
  sendEmailVerification,
  signOut,
  onAuthStateChanged,
  fetchSignInMethodsForEmail} from "firebase/auth";
import { 
  doc, 
  getDoc, 
  getDocs,
  setDoc,
  collection as firestoreCollection,
  getFirestore,
  query,
  where,
  updateDoc} from "firebase/firestore";

// Import 2FA Components
import TwoFactorModal from "../../../components/UI/organisms/modal/TwoFactorModal";

// Import Atoms
import TextFieldComp from "../../../components/UI/atoms/text-fields/TextFieldComp";

// Import Molecules
import AlertComp from "../../../components/UI/molecules/alert/AlertComp";

// Import Assets
import stars from "../../../assets/stars.png";
import back from "../../../assets/back.png";
import logo from "../../../assets/logo.png";

// Imports MUI Components
import {  Box,
          Button,
          Stack,
          Typography,
          CircularProgress } from "@mui/material"


// Import Functions
import { fetchUserDetails } from "../../../components/functions/fetchUserDetails";
import { notifyFirstUser } from "../../../components/functions/notifyFirstUser";

// Import CSS
import "./styles.css"
import Icon from '../../../components/UI/atoms/icon/Icon';
import Snackbar from '../../../components/UI/atoms/snackbar/Snackbar';

// function TutorialOverlay({ rect, onClose }) {
//   const overlayStyle = {
//     position: 'fixed',
//     top: 0,
//     left: 0,
//     width: '100vw',
//     height: '100vh',
//     backgroundColor: 'rgba(0,0,0,0.6)',
//     zIndex: 999,
//   };

//   const highlightStyle = {
//     position: 'absolute',
//   top: rect.top - 22.5,
//   left: rect.left - 5,
//   width: rect.width + 10,
//   height: rect.height + 10,
//   border: '2px solid #4fc3f7',
//   boxShadow: '0 0 10px #4fc3f7',
//   zIndex: 1000,
//   pointerEvents: 'none'
//   };

//   const tooltipStyle = {
//      position: 'fixed',
//   top: rect.top + rect.height + 12,
//   left: rect.left,
//   backgroundColor: '#4fc3f7',
//   color: '#fff',
//   padding: '10px',
//   borderRadius: '6px',
//   zIndex: 1001,
//   };

//   return (
//     <>
//       <div style={overlayStyle} onClick={onClose}></div>
//       <div style={highlightStyle}></div>
//       <div style={tooltipStyle}>
//         <p>Let’s start by signing in to your account. Click here to sign in.</p>
//         <button onClick={onClose} 
//           style={{
//             marginTop: '8px',
//             background: '#fff',
//             color: '#4fc3f7',
//             border: 'none',
//             padding: '6px 12px',
//             borderRadius: '4px',
//             hover: {
//               background: '#4fc3f7',
//               color: '#fff',
//               cursor: "pointer"
//             }
//           }}>
//           Got it
//         </button>
//       </div>
//     </>
//   );
// }

// Helper function to normalize mobile numbers to standard format (09XXXXXXXXX)
const normalizeMobileNumber = (input) => {
  if (!input) return null;
  
  // Remove all non-digit characters (spaces, hyphens, parentheses, plus signs)
  let digits = input.replace(/\D/g, '');
  
  // Handle different formats:
  // 0919-555-4383 → 09195554383
  // 0919 555 4383 → 09195554383
  // (0919) 555 4383 → 09195554383
  // +639195554383 → 09195554383
  // +63-919-555-4383 → 09195554383
  
  // If starts with country code +63 or 63, remove it
  if (digits.startsWith('63')) {
    digits = digits.substring(2);
  }
  
  // If starts with 9 and has 10 digits, add leading 0
  if (digits.startsWith('9') && digits.length === 10) {
    digits = '0' + digits;
  }
  
  // Validate final format is 09XXXXXXXXX (11 digits starting with 09)
  if (/^09\d{9}$/.test(digits)) {
    return digits;
  }
  
  return null; // Invalid format
};

const SignIn = () => {
  // ADDED
  const signInRef = useRef(null);
  const [rect, setRect] = useState(true);
  const [showTutorial, setShowTutorial] = useState(true);

  // Initialization State variables
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [userRole, setUserRole] = useState("");
  const [noInput, setNoInput] = useState(false);
  const navigate = useNavigate();
  const location = useLocation();
  const auth = getAuth(app);
  
  // Helper function to navigate to dashboard while preserving query params from location state
  const navigateToDashboard = (userType = null) => {
    let dashboardRoute = "/dashboard";
    
    // Check if we came from a protected route with query params
    const fromLocation = location.state?.from;
    const fromSearch = location.state?.search;
    
    // If we have preserved search params (like ?isCanceling=true), append them
    if (fromSearch) {
      dashboardRoute = `/dashboard${fromSearch}`;
    }
    
    const state = userType ? { userRole: userType } : {};
    navigate(dashboardRoute, { state, replace: true });
  };

  // Signing state defined early to avoid TDZ issues
  const [isSigningIn, setIsSigningIn] = useState(false);

  const [showCustomAlert, setShowCustomAlert] = useState(false);
  const [showSplash, setShowSplash] = useState(false);
  const [snackbarMessage, setSnackbarMessage] = useState("");
  const [showSnackbar, setShowSnackbar] = useState(false);
  const [alertMessage, setAlertMessage] = useState("");
  const [alertTitle, setAlertTitle] = useState("")
  const [alertImage, setAlertImage] = useState("");
  const [alertButton, setAlertButton] = useState("")
  const [alertButtonType, setAlertButtonType] = useState("false")
  const [alertLink, setAlertLink] = useState("")
  const [userDetails, setUserDetails] = useState(null);

  // 2FA related state
  const [show2FAModal, setShow2FAModal] = useState(false);
  const [pendingUser, setPendingUser] = useState(null);
  const [pendingUserData, setPendingUserData] = useState(null);
  const [pendingUserRole, setPendingUserRole] = useState("");
  const [isClosing2FA, setIsClosing2FA] = useState(() => {
    // Initialize from sessionStorage in case of page refresh
    return sessionStorage.getItem("closing2FA") === "true";
  });

  // UseEffects
  useEffect(() => {
    const details = fetchUserDetails(setUserDetails);
    // const redirect = redirectIfNotLoggedIn(setShowCustomAlert, navigate);
    const notify = notifyFirstUser();
    
    // Clean up closing2FA flag if user is not authenticated
    if (!auth.currentUser && sessionStorage.getItem("closing2FA") === "true") {
      sessionStorage.removeItem("closing2FA");
      setIsClosing2FA(false);
    }
    
    return () => {
      details && details();
      // redirect && redirect();
      notify && notify();
    };
  }, [auth]);

  useEffect(() => {
    setShowSplash(true);

    const timeout = setTimeout(() => {
      const user = auth.currentUser;
      const userId = user?.uid;
      if (!userId) {
        setShowSplash(false);
      } else {
        setShowSplash(false);
      }
    }, 1000);
    return () => clearTimeout(timeout);
  }, []);


  // Check for session expired message from location state or sessionStorage
  useEffect(() => {
    // First check location state
    const state = location.state;
    if (state?.sessionExpired && state?.message) {
      setSnackbarMessage(state.message);
      setShowSnackbar(true);
      // Clear the state to prevent showing the message again on refresh
      setTimeout(() => {
        window.history.replaceState({}, document.title);
      }, 100);
      return;
    }
    
    // Fallback: check sessionStorage (in case React Router doesn't detect navigation)
    const sessionExpiredMessage = sessionStorage.getItem("sessionExpiredMessage");
    if (sessionExpiredMessage) {
      setSnackbarMessage(sessionExpiredMessage);
      setShowSnackbar(true);
      // Clear from sessionStorage
      sessionStorage.removeItem("sessionExpiredMessage");
      // Clear the state if it exists
      setTimeout(() => {
        window.history.replaceState({}, document.title);
      }, 100);
    }
  }, [location]);

  // Check if user was redirected here for 2FA verification
  // Also check if user just signed in and needs 2FA
  useEffect(() => {
    const user = auth.currentUser;
    const require2FA = location.state?.require2FA;
    const pending2FA = sessionStorage.getItem("pending2FA");
    
    // Prevent duplicate modal triggers:
    // - Don't show if modal is already showing
    // - Don't show if pending2FA is already set (modal should be shown by handleSignIn)
    // - Don't show if user is currently signing in (let handleSignIn handle it)
    if (user && !pending2FA && require2FA && !show2FAModal && !isSigningIn) {
      // Fetch user data to check 2FA status and show modal
      const checkAndShow2FA = async () => {
        try {
          const db = getFirestore(app);
          const userRef = doc(db, "users", user.uid);
          const userSnap = await getDoc(userRef);
          
          if (userSnap.exists()) {
            const userData = userSnap.data();
            const is2FAEnabled = userData.twoFactorEnabled === true;
            
            // Double-check that modal isn't already showing and pending2FA isn't set
            if (is2FAEnabled && !sessionStorage.getItem("pending2FA") && !show2FAModal) {
              sessionStorage.setItem("pending2FA", user.uid);
              setPendingUser(user);
              setPendingUserData(userData);
              setPendingUserRole(userData.userType || userData.role);
              setShow2FAModal(true);
            }
          }
        } catch (error) {
          console.error("Error checking 2FA status:", error);
        }
      };
      
      checkAndShow2FA();
    }
  }, [auth, location.state, show2FAModal, isSigningIn]);

  // ADDED
  useEffect(() => {
    if (signInRef.current) {
      requestAnimationFrame(() => {
      setRect(signInRef.current.getBoundingClientRect());
    });
    }
  }, []);

  // Function to handle sign in
  const handleSignIn = async () => {
    // Client-side attempt tracking (approximate - Firebase does not expose real remaining count)
    const ATTEMPT_LIMIT = 5;
    const getAttemptsKey = (id) => `signinAttempts:${id}`;
    const getAttempts = (id) => {
      try {
        const raw = localStorage.getItem(getAttemptsKey(id));
        const data = raw ? JSON.parse(raw) : null;
        if (!data) return 0;
        // reset after 15 minutes
        if (Date.now() - (data.ts || 0) > 15 * 60 * 1000) {
          localStorage.removeItem(getAttemptsKey(id));
          return 0;
        }
        return Number(data.count || 0);
      } catch {
        return 0;
      }
    };
    const recordFailure = (id) => {
      try {
        const next = getAttempts(id) + 1;
        localStorage.setItem(getAttemptsKey(id), JSON.stringify({ count: next, ts: Date.now() }));
        return Math.max(0, ATTEMPT_LIMIT - next);
      } catch {
        return null;
      }
    };
    const resetAttempts = (id) => {
      try { localStorage.removeItem(getAttemptsKey(id)); } catch {}
    };
    const trimmedEmail = email.trim();
    
    if (!trimmedEmail || !password) {
      setNoInput(true);
      return;
    }

    setIsSigningIn(true);

    try {
      const db = getFirestore(app);
      
      // Normalize mobile number input (handles various formats)
      const normalizedMobile = normalizeMobileNumber(trimmedEmail);
      const isMobile = normalizedMobile !== null;
      const idLabel = isMobile ? "Mobile number" : "Email address";
      
      // Use normalized mobile number for queries
      const searchIdentifier = isMobile ? normalizedMobile : trimmedEmail;
      
      // Flag to track if we've already authenticated the user
      let skipEmailChecks = false;
      
      if (!isMobile) {
        // Check if this email is a pendingEmailChange (mobile-only user adding email)
        const pendingEmailQuery = query(
          firestoreCollection(db, "users"),
          where("pendingEmailChange", "==", trimmedEmail)
        );
        const pendingSnapshot = await getDocs(pendingEmailQuery);
        
        if (!pendingSnapshot.empty) {
          // Found user with pending email
          const userData = pendingSnapshot.docs[0].data();
          const userId = pendingSnapshot.docs[0].id;
          const userAuthEmail = userData.authEmail; // Mobile dummy email
          const userMobile = userData.mobile;
          const currentVerifiedEmail = userData.email || ""; // existing verified email is retained during email-change
          
          // First, try to sign in with the pending email (in case it's been verified)
          // Prevent dashboard auto-redirects while we probe verification status
          sessionStorage.setItem('suppressPostSignInRedirect', 'true');
          try {
            const credential = await signInWithEmailAndPassword(auth, trimmedEmail, password);
            
              // Success! The email has been verified in Firebase Auth
              // Now we need to sync Firestore
              if (credential.user.emailVerified) {
                const userRef = doc(db, "users", userId);
                await updateDoc(userRef, {
                  authEmail: trimmedEmail,
                  email: trimmedEmail,
                  emailVerified: true,
                  pendingEmailChange: null,
                  emailChangeStatus: null,
                  emailChangeTimestamp: null,
                  // Reset 2FA when email changes for security
                  twoFactorEnabled: false,
                  twoFactorMethod: null,
                  twoFASessionVerified: false,
                  twoFASessionExpiry: null
                });
                
                // Email verified — allow normal redirects and continue to normal flow (2FA is now disabled)
                sessionStorage.removeItem('suppressPostSignInRedirect');
                // User is already authenticated, skip further email checks
                skipEmailChecks = true;
                authEmail = trimmedEmail;
                // Don't return - continue to normal sign-in flow
            } else {
              // Email exists in Firebase Auth but not verified yet
              if (!userMobile && !currentVerifiedEmail) {
                // No mobile on file anymore → redirect to verify-email page
                setIsSigningIn(false);
                sessionStorage.setItem('suppressPostSignInRedirect', 'true');
                navigate('/verify-email', { 
                  state: { 
                    email: trimmedEmail, 
                    userId: userId,
                    fromSignIn: true,
                    hasMobile: false
                  },
                  replace: true
                });
                return;
              }
              // Has mobile → show sticky snackbar with guidance
              // Prevent any auto-redirects triggered by auth state changes
              sessionStorage.setItem('suppressPostSignInRedirect', 'true');
              try { await auth.signOut(); } catch (_) {}
              const suggestedIdentifier = (() => {
                // Prefer a verified email only
                const isEmailVerified = userData.emailVerified === "Yes" || userData.emailVerified === true;
                if (currentVerifiedEmail && isEmailVerified) return currentVerifiedEmail;
                // Otherwise suggest the mobile number if available
                if (userMobile) return userMobile;
                // Fallback generic text
                return "your verified email";
              })();
              const mobileNote = userMobile && suggestedIdentifier !== userMobile ? ` or ${userMobile}` : "";
              setShowSnackbar(true);
              setSnackbarMessage(`Email not yet verified. Please verify your email or sign-in as ${suggestedIdentifier}${mobileNote}.`);
              setIsSigningIn(false);
              return;
            }
          } catch (pendingAuthError) {
            // Pending email doesn't exist in Firebase Auth yet, or wrong password
            // Try with mobile dummy email to validate password
            try {
              // Still suppress during this probe
              sessionStorage.setItem('suppressPostSignInRedirect', 'true');
              await signInWithEmailAndPassword(auth, userAuthEmail, password);
              // Password correct - but user tried to sign in with unverified pending email
              
              // Sign out the user immediately - they must verify the email first
              try { await auth.signOut(); } catch (_) {}
              
              // Clear suppression flag
              sessionStorage.removeItem('suppressPostSignInRedirect');
              
              // Show message telling them to verify or use verified credentials
              const suggestedIdentifier = (() => {
                const isEmailVerified = userData.emailVerified === "Yes" || userData.emailVerified === true;
                if (currentVerifiedEmail && isEmailVerified) return currentVerifiedEmail;
                if (userMobile) return userMobile;
                return "your verified credentials";
              })();
              const hasMobileMsg = userMobile && suggestedIdentifier !== userMobile ? ` or ${userMobile}` : "";
              setShowSnackbar(true);
              setSnackbarMessage(`Email not yet verified. Please verify your email or sign-in as ${suggestedIdentifier}${hasMobileMsg}.`);
              setIsSigningIn(false);
              return;
            } catch (authError) {
              // Clear suppression flag before showing error
              sessionStorage.removeItem('suppressPostSignInRedirect');
              setShowSnackbar(true);
              if (authError?.code === "auth/too-many-requests") {
                setSnackbarMessage("Too many sign-in attempts. Try again later.");
              } else {
                setSnackbarMessage(`Incorrect ${idLabel.toLowerCase()} or password. Please try again.`);
              }
              setTimeout(() => setShowSnackbar(false), 5000);
              setIsSigningIn(false);
              return;
            }
          }
        }
        
        // Signing in with email - check if email exists and is unverified
        // Skip this check if we already authenticated above
        if (!skipEmailChecks) {
          // Query for users with this email (Firestore may store emailVerified as false, "No", or "Yes")
          const emailQuery = query(
            firestoreCollection(db, "users"),
            where("email", "==", trimmedEmail)
          );
          const emailSnapshot = await getDocs(emailQuery);
          
          if (!emailSnapshot.empty) {
          // Found user with this email - check verification status
          const userData = emailSnapshot.docs[0].data();
          const userId = emailSnapshot.docs[0].id;
          const userMobile = userData.mobile;
          const emailVerified = userData.emailVerified;
          
          // Check if email is unverified (could be false, "No", or any value other than "Yes", "MobileOnly")
          const isUnverified = emailVerified !== "Yes" && 
                               emailVerified !== "MobileOnly" && 
                               emailVerified !== true;
          
          if (isUnverified) {
            // User has unverified email, try to sign in to validate password
            try {
              // Suppress redirects during verification probe
              sessionStorage.setItem('suppressPostSignInRedirect', 'true');
              const credential = await signInWithEmailAndPassword(auth, trimmedEmail, password);
              
              // Check if email is now verified in Firebase Auth
              if (credential.user.emailVerified) {
                // Email verified! Update Firestore
                const userRef = doc(db, "users", userId);
                await updateDoc(userRef, {
                  emailVerified: "Yes"
                });
                
                // Allow normal redirects now that user is verified
                sessionStorage.removeItem('suppressPostSignInRedirect');
                // User is already authenticated, skip further checks
                skipEmailChecks = true;
                // Don't return - continue to 2FA check
              } else {
                // Email not verified
                if (userMobile) {
                  // If user has mobile on file, suggest signing in with mobile instead of redirecting
                  // Keep suppression flag while we sign out to avoid bounce
                  sessionStorage.setItem('suppressPostSignInRedirect', 'true');
                  try { await auth.signOut(); } catch (_) {}
                  setShowSnackbar(true);
                  setSnackbarMessage(`Email not yet verified. Please verify your email or sign-in as ${userMobile}.`);
                  setIsSigningIn(false);
                  return;
                }
                // No mobile available → redirect to verify-email page
                setIsSigningIn(false);
                sessionStorage.setItem('suppressPostSignInRedirect', 'true');
                navigate('/verify-email', { 
                  state: { 
                    email: trimmedEmail, 
                    userId: userId,
                    fromSignIn: true,
                    hasMobile: false
                  },
                  replace: true
                });
                return;
              }
            } catch (authError) {
              // Password is incorrect or other auth error
              setShowSnackbar(true);
              if (authError?.code === "auth/too-many-requests") {
                setSnackbarMessage("Too many sign-in attempts. Try again later.");
              } else {
                setSnackbarMessage(`Incorrect ${idLabel.toLowerCase()} or password. Please try again.`);
              }
              setTimeout(() => setShowSnackbar(false), 5000);
              setIsSigningIn(false);
              return;
            }
          } else {
            // Email is verified, continue with normal sign-in flow
            // Clear any suppression flags from previous checks
            sessionStorage.removeItem('suppressPostSignInRedirect');
          }
          // If email is verified, continue with normal sign-in flow below
          } else {
            // Email not found in Firestore, continuing to sign-in flow
            // Clear any suppression flags - this is a normal sign-in
            sessionStorage.removeItem('suppressPostSignInRedirect');
          }
        }
      }
      
      // Determine if the identifier is a mobile number or email
      let authEmail = trimmedEmail;
      // Check if user signed in with mobile (from sessionStorage or current attempt)
      let mobileSignInSuccess = sessionStorage.getItem("mobileSignIn") === "true" || false; // Track if mobile sign-in was successful
      let updatedLocalUserData = null; // Track updated userData after mobile sign-in
      
      // PREEMPTIVELY set pending2FA if we detect this might be a mobile sign-in with 2FA
      // This prevents ProtectedRoute from redirecting and triggering duplicate modals
      if (isMobile) {
        // Check if this user might have 2FA enabled BEFORE signing in
        // to prevent race condition with ProtectedRoute
        try {
          const db = getFirestore(app);
          const q = query(firestoreCollection(db, "users"), where("mobile", "==", searchIdentifier));
          const snapshot = await getDocs(q);
          if (!snapshot.empty) {
            const userData = snapshot.docs[0].data();
            if (userData.twoFactorEnabled === true) {
              // Pre-emptively set pending2FA to prevent ProtectedRoute redirect
              // We'll update it with the actual UID after sign-in
              sessionStorage.setItem("pending2FA", "pending");
            }
          }
        } catch (error) {
          // Ignore errors - we'll check again after sign-in
        }
      }

      // Check if account is deleted BEFORE attempting sign-in
      // Redirect to sign-up for reactivation within 30 days
      if (!isMobile) {
        try {
          const db = getFirestore(app);
          const deletedQuery = query(
            firestoreCollection(db, "users"), 
            where("email", "==", trimmedEmail),
            where("accountDeleted", "==", true)
          );
          const deletedSnapshot = await getDocs(deletedQuery);
          
          if (!deletedSnapshot.empty) {
            const deletedDoc = deletedSnapshot.docs[0];
            const deletedData = deletedDoc.data();
            const deletedAt = deletedData.deletedAt?.toDate();
            
            // Check if within 30-day grace period
            if (deletedAt) {
              const daysSinceDeletion = Math.floor((new Date() - deletedAt) / (1000 * 60 * 60 * 24));
              const daysRemaining = 30 - daysSinceDeletion;
              
              if (daysSinceDeletion > 30) {
                // Grace period expired
                setShowSnackbar(true);
                setSnackbarMessage("This account was deleted more than 30 days ago and can no longer be reactivated. Please sign up to create a new account.");
                setTimeout(() => setShowSnackbar(false), 8000);
                setIsSigningIn(false);
                return;
              } else {
                // Within grace period - allow sign-in for reactivation
                console.log(`Deleted account within grace period (${daysRemaining} days remaining). Allowing sign-in for reactivation.`);
                // Continue with sign-in...
              }
            } else {
              // No deletion timestamp - allow sign-in
              console.log("Deleted account has no timestamp. Allowing sign-in for reactivation.");
            }
          }
        } catch (error) {
          console.error("Error checking for deleted account:", error);
          // Continue with sign-in attempt
        }
      }

      // If mobile number, look up authEmail from Firestore
      if (isMobile) {
        const q = query(firestoreCollection(db, "users"), where("mobile", "==", searchIdentifier));
        const snapshot = await getDocs(q);
        
        if (!snapshot.empty) {
          let userData = snapshot.docs[0].data();
          const userId = snapshot.docs[0].id;
          
          // Additional check: Make sure mobile field is not empty
          // This prevents sign-in if mobile was removed from profile
          if (!userData.mobile || userData.mobile.trim() === "") {
            setShowSnackbar(true);
            setSnackbarMessage(`${idLabel} does not exist. Please sign up first.`);
            setTimeout(() => setShowSnackbar(false), 5000);
            setIsSigningIn(false);
            return;
          }
          
          // Choose which email to try first for mobile login.
          // Default: use authEmail (dummy mobile email). If pendingEmailChange exists and
          // Firebase reports it has a password sign-in method, try that first.
          let primaryEmailToUse = userData.authEmail;
          let alternateEmailToUse = userData.pendingEmailChange || null;
          if (userData.pendingEmailChange) {
            try {
              const methods = await fetchSignInMethodsForEmail(getAuth(app), userData.pendingEmailChange);
              if (methods && methods.includes("password")) {
                primaryEmailToUse = userData.pendingEmailChange;
                alternateEmailToUse = userData.authEmail;
              }
            } catch (_) {
              // if probe fails, keep default order
            }
          }

          const tryEmail = async (emailToUse) => {
            const cred = await signInWithEmailAndPassword(auth, emailToUse, password);
            return cred;
          };

          try {
            const testCredential = await tryEmail(primaryEmailToUse);
            const user = testCredential.user;
            
            // Check if email is unverified and move to pendingEmailChange
            const userRef = doc(db, "users", userId);
            
            // If email is NOT verified, move it to pendingEmailChange
            if (!user.emailVerified && userData.email && userData.emailVerified !== "Yes" && userData.emailVerified !== "MobileOnly") {
              const originalEmail = userData.email;
              await updateDoc(userRef, {
                email: "", // Clear email field
                pendingEmailChange: originalEmail, // Store unverified email
                emailChangeStatus: "pending",
                emailChangeTimestamp: Date.now(),
                emailVerified: "No"
              });
              
              // Immediately re-fetch to get the updated data
              const updatedSnap = await getDoc(userRef);
              if (updatedSnap.exists()) {
                updatedLocalUserData = updatedSnap.data();
                userData = updatedLocalUserData;
              }
            } else {
              // Immediately sync Firestore if Firebase Auth email differs
              if (userData.authEmail !== user.email && user.emailVerified) {
                await updateDoc(userRef, {
                  authEmail: user.email,
                  email: user.email,
                  emailVerified: true,
                  pendingEmailChange: null,
                  emailChangeStatus: null,
                  emailChangeTimestamp: null
                });
              }
            }
            
            // Mark mobile sign-in as successful
            mobileSignInSuccess = true;
            sessionStorage.setItem("mobileSignIn", "true");
            
          } catch (authError) {
            // If primary email fails and an alternate is available, try it before failing
            const alternate = alternateEmailToUse;
            if (alternate) {
              try {
                const altCredential = await tryEmail(alternate);
                const user = altCredential.user;
                const userRef = doc(db, "users", userId);
                // If alternate (likely authEmail) succeeded but Firebase has a verified email, sync
                if (userData.authEmail !== user.email && user.emailVerified) {
                  await updateDoc(userRef, {
                    authEmail: user.email,
                    email: user.email,
                    emailVerified: true,
                    pendingEmailChange: null,
                    emailChangeStatus: null,
                    emailChangeTimestamp: null
                  });
                }
                mobileSignInSuccess = true;
                sessionStorage.setItem("mobileSignIn", "true");
              } catch (finalErr) {
                // Both attempts failed → show appropriate message
                setShowSnackbar(true);
                if (finalErr?.code === "auth/too-many-requests") {
                  setSnackbarMessage("Too many sign-in attempts. Try again later.");
                } else {
                  setSnackbarMessage(`Incorrect ${idLabel.toLowerCase()} or password. Please try again.`);
                }
                setTimeout(() => setShowSnackbar(false), 5000);
                setIsSigningIn(false);
                return;
              }
            } else {
              // No alternate email to try; show error
              setShowSnackbar(true);
              if (authError?.code === "auth/too-many-requests") {
                setSnackbarMessage("Too many sign-in attempts. Try again later.");
              } else {
                setSnackbarMessage(`Incorrect ${idLabel.toLowerCase()} or password. Please try again.`);
              }
              setTimeout(() => setShowSnackbar(false), 5000);
              setIsSigningIn(false);
              return;
            }
          }
        } else {
          setShowSnackbar(true);
          setSnackbarMessage(`${idLabel} does not exist. Please sign up first.`);
          setTimeout(() => setShowSnackbar(false), 5000);
          setIsSigningIn(false);
          return;
        }
      }

      // Sign in the user with the provided email and password (for non-mobile sign-ins)
      let userCredential;
      let user;
      
      // Check if user is already authenticated (from email verification check above)
      const alreadyAuthenticated = auth.currentUser !== null;
      
      if (!isMobile) {
        if (alreadyAuthenticated && skipEmailChecks) {
          // User already authenticated from email verification check, use current user
          user = auth.currentUser;
        } else {
          // Normal email sign-in
          try {
            userCredential = await signInWithEmailAndPassword(auth, authEmail, password);
            user = userCredential.user;
          } catch (normErr) {
            // If sign-in with typed email fails, check if this email is still stored as pendingEmailChange
            try {
              const fallbackPendingQ = query(firestoreCollection(db, "users"), where("pendingEmailChange", "==", trimmedEmail));
              const fallbackSnap = await getDocs(fallbackPendingQ);
              if (!fallbackSnap.empty) {
                const fbData = fallbackSnap.docs[0].data();
                const fbAuthEmail = fbData.authEmail;
                try {
                  const fbCred = await signInWithEmailAndPassword(auth, fbAuthEmail, password);
                  user = fbCred.user;
                  // If the account primary email is now verified and different from authEmail, sync Firestore
                  const fbUserRef = doc(db, "users", fallbackSnap.docs[0].id);
                  if (user.email && user.email !== fbData.authEmail && user.emailVerified) {
                    await updateDoc(fbUserRef, {
                      authEmail: user.email,
                      email: user.email,
                      emailVerified: true,
                      pendingEmailChange: null,
                      emailChangeStatus: null,
                      emailChangeTimestamp: null,
                    });
                  }
                } catch (fbErr) {
                  // rethrow to be handled by existing error flow
                  throw fbErr;
                }
              } else {
                throw normErr;
              }
            } catch (finalErr) {
              // propagate to outer catch where messaging is handled
              throw finalErr;
            }
          }
        }
      } else {
        // Mobile sign-in already handled above, get current user
        user = auth.currentUser;
        
        if (!user) {
          setShowSnackbar(true);
          setSnackbarMessage("Sign-in failed. Please try again.");
          setTimeout(() => setShowSnackbar(false), 5000);
          setIsSigningIn(false);
          return;
        }
      }

      // For mobile sign-in with updated local data, use it if available
      // Otherwise, fetch from Firestore
      const userRef = doc(db, "users", user.uid);
      let finalUserData;
      let reactivatedFromDeleted = false;
      
      if (isMobile && mobileSignInSuccess && updatedLocalUserData) {
        // Use the updated local data that already has pendingEmailChange set
        finalUserData = updatedLocalUserData;
      } else {
        // Fetch from Firestore
        const userSnap = await getDoc(userRef);
        if (userSnap.exists()) {
          finalUserData = userSnap.data();
          
          // Check if account is marked as deleted
          if (finalUserData.accountDeleted === true) {
            console.log("Found deleted account for user:", user.uid);
            
            // Check if Firebase Auth email is verified before reactivating
            if (user.email && !user.emailVerified && !user.email.includes("@mobileuser.dentalapp.com")) {
              console.log("Email not verified, redirecting to verify-email page");
              
              // Send verification email
              await sendEmailVerification(user);
              
              // Redirect to verification page
              navigate('/verify-email', { 
                state: { 
                  from: '/sign-in',
                  email: user.email,
                  isReactivation: true,
                  message: "Please verify your email to reactivate your account and restore your treatment history."
                },
                replace: true 
              });
              return;
            }
            
            // Email is verified (or mobile-only account), proceed with reactivation
            console.log("Email verified, reactivating deleted account");
            
            // Reactivate the account
            await setDoc(userRef, {
              accountDeleted: false,
              accountStatus: 'active',
              reactivatedAt: new Date().toISOString(),
              lastModified: new Date().toISOString(),
              emailVerified: user.emailVerified ? "Yes" : finalUserData.emailVerified,
            }, { merge: true });
            
            // Update local data to reflect reactivation
            finalUserData = {
              ...finalUserData,
              accountDeleted: false,
              accountStatus: 'active',
              reactivatedAt: new Date().toISOString(),
              emailVerified: user.emailVerified ? "Yes" : finalUserData.emailVerified,
            };
            
            reactivatedFromDeleted = true;
            console.log("Successfully reactivated account");
          }
        } else {
          // User doc not found with current UID - check if deleted account exists by email/mobile
          const email = user.email;
          const usersRef = firestoreCollection(db, "users");
          
          // Try to find deleted account by auth email
          if (email && !email.includes("@mobileuser.dentalapp.com")) {
            const deletedByEmailQuery = query(
              usersRef,
              where("email", "==", email),
              where("accountDeleted", "==", true)
            );
            const deletedByEmailSnapshot = await getDocs(deletedByEmailQuery);
            
            if (!deletedByEmailSnapshot.empty) {
              const deletedDoc = deletedByEmailSnapshot.docs[0];
              const deletedData = deletedDoc.data();
              const oldDocId = deletedDoc.id;
              
              console.log("Found deleted account by email:", oldDocId);
              
              // Check if Firebase Auth email is verified before reactivating
              if (user.email && !user.emailVerified && !user.email.includes("@mobileuser.dentalapp.com")) {
                console.log("Email not verified, redirecting to verify-email page");
                
                // Send verification email
                await sendEmailVerification(user);
                
                // Redirect to verification page
                navigate('/verify-email', { 
                  state: { 
                    from: '/sign-in',
                    email: user.email,
                    isReactivation: true,
                    message: "Please verify your email to reactivate your account and restore your treatment history."
                  },
                  replace: true 
                });
                return;
              }
              
              // Email is verified (or mobile-only account), proceed with reactivation
              console.log("Email verified, but deleted account has mismatched document ID");
              
              // ERROR: Cannot reactivate account with mismatched document ID
              // This would lose all treatment history (subcollections)
              // Sign out and show error message
              await signOut(auth);
              setShowSnackbar(true);
              setSnackbarMessage("Account reactivation failed. Please contact support to restore your account and treatment history.");
              setTimeout(() => setShowSnackbar(false), 6000);
              setIsSigningIn(false);
              return;
            } else {
              throw new Error("User data not found in Firestore");
            }
          } else {
            throw new Error("User data not found in Firestore");
          }
        }
      }
      
      if (finalUserData) {
        const userData = finalUserData; // Use finalUserData consistently
        const emailVerified = userData.emailVerified;

        // Skip email verification check if:
        // 1. User signed in with mobile number (isMobile = true OR mobileSignInSuccess = true)
        // 2. User is mobile-only (dummy email)
        // 3. User has pendingEmailChange (email is in pending state)
        const isMobileOnlyAccount = authEmail.includes("@mobileuser.dentalapp.com");
        const hasPendingEmail = userData.pendingEmailChange && (!userData.email || userData.email.trim() === "");
        
        // CRITICAL: Skip ALL email verification redirects for mobile sign-ins
        // This includes users who just signed in with mobile (mobileSignInSuccess = true)
        // OR users who have unverified email and are signing in with mobile
        const hasUnverifiedEmail = userData.email && emailVerified !== "MobileOnly" && emailVerified !== "Yes" && user && !user.emailVerified;
        
        // CRITICAL: Mobile sign-ins should NEVER redirect to verify-email
        // Check this FIRST before any email verification checks
        if (isMobile) {
          // Continue to dashboard - don't redirect
        } else if (mobileSignInSuccess || isMobileOnlyAccount || hasPendingEmail) {
          // Mobile sign-in or has pending email - skip verification check completely
        } else if (hasUnverifiedEmail && user) {
          // If user has a mobile on file, do NOT redirect — sign out and show snackbar
          const userMobile = userData.mobile;
          if (userMobile && userMobile.trim() !== '') {
            // Keep suppression active to avoid any route bounces while signing out
            sessionStorage.setItem('suppressPostSignInRedirect', 'true');
            try { await auth.signOut(); } catch (_) {}
            setShowSnackbar(true);
            setSnackbarMessage(`Email not yet verified. Please verify your email or sign-in as ${userMobile}.`);
            setIsSigningIn(false);
            return;
          }
          // Otherwise, proceed to verify-email flow
          await sendEmailVerification(user);
          navigate('/verify-email', { 
            state: { 
              from: '/sign-in',
              email: user.email
            },
            replace: true 
          });
          return;
        }
      }

      let userType = ""; // Temporary variable to determine the user type
     
      if (finalUserData) {
        const userData = finalUserData; // Use the same finalUserData
        userType = userData.userType || userData.role; // Check both userType and role fields
        
        if (userType) {
          // Check if 2FA is enabled for this user
          const is2FAEnabled = userData.twoFactorEnabled === true;
        
          if (is2FAEnabled) {
            // Prevent duplicate modal triggers - check if modal is ACTUALLY showing
            // Don't just rely on sessionStorage as it might be stale
            const existingPending2FA = sessionStorage.getItem("pending2FA");
            if (existingPending2FA === user.uid && show2FAModal) {
              // Modal is already showing for this user, don't trigger again
              setIsSigningIn(false);
              return;
            }
            
            // Clear any stale pending2FA first, then set the current user
            sessionStorage.removeItem("pending2FA");
            sessionStorage.setItem("pending2FA", user.uid);
            // Store user data for 2FA verification
            setPendingUser(user);
            setPendingUserData(userData);
            setPendingUserRole(userType);
            setShow2FAModal(true);
            setIsSigningIn(false); // Stop loading state - wait for 2FA verification
            return; // Don't navigate yet - wait for 2FA verification
          } else {
            // 2FA not enabled - clear any suppression flags and navigate normally
            sessionStorage.removeItem('suppressPostSignInRedirect');
            
            setUserRole(userType); // Store the user role in state

            // Show success message if account was reactivated
            if (reactivatedFromDeleted) {
              setShowSnackbar(true);
              setSnackbarMessage("Welcome back! Your account has been reactivated with all your treatment history restored.");
              setTimeout(() => setShowSnackbar(false), 5000);
            }

            // Navigate to the appropriate dashboard (preserving query params from email link)
            navigateToDashboard(userType);
          }
        } else {
          setAlertMessage("User type not determined. Please contact support.");
          setAlertTitle("Sign In Failed");
          setAlertImage("caution");
          setAlertButton("Try Again")
          setAlertLink("none")
          setAlertButtonType(false)
          setShowCustomAlert(true);
        }
      } else {
        setAlertMessage("User type not determined. Please contact support.");
        setAlertTitle("Sign In Failed");
        setAlertImage("caution");
        setAlertButton("Try Again")
        setAlertLink("none")
        setAlertButtonType(false)
        setShowCustomAlert(true);
      }
    } catch (error) {
      console.error("Error signing in:", error);
      
      // Check for too-many-requests error first
      if (error?.code === "auth/too-many-requests") {
        setShowSnackbar(true);
        setSnackbarMessage("Too many sign-in attempts. Try again later.");
        setTimeout(() => setShowSnackbar(false), 5000);
        setIsSigningIn(false);
        return;
      }
      
      // Check if user exists in Firestore but email is not verified
      try {
        const db = getFirestore(app);
        const usersRef = firestoreCollection(db, 'users');
        const snapshot = await getDocs(usersRef);
        
        if (!snapshot.empty) {
          const usersList = [];
          snapshot.forEach((doc) => {
            usersList.push({ id: doc.id, ...doc.data() });
          });
          
          // Find a user with matching email
          const userMatch = usersList.find(
            (val) => val.email === trimmedEmail
          );
          
          // If user exists and we got here, the credential is likely invalid -> show incorrect password message
          if (userMatch) {
            const idLbl = /^09\\d{9}$/.test(trimmedEmail) ? "Mobile number" : "Email address";
            setShowSnackbar(true);
            setSnackbarMessage(`Incorrect ${idLbl.toLowerCase()} or password. Please try again.`);
            setTimeout(() => setShowSnackbar(false), 5000);
            setIsSigningIn(false);
            return;
          }
          
          if (userMatch && userMatch.emailVerified === "No") {
            // User exists but email is not verified, redirect to verify email page
            navigate('/verify-email', { 
              state: { 
                from: '/sign-in',
                email: trimmedEmail
              },
              replace: true 
            });
            return;
          }
        }
        
        // If normal sign-in fails, check pendingPersonnel
        const pendingRef = firestoreCollection(db, 'pendingPersonnel');
        const pendingSnapshot = await getDocs(pendingRef);
        if (!pendingSnapshot.empty) {
          const pendingList = [];
          pendingSnapshot.forEach((doc) => {
            pendingList.push({ id: doc.id, ...doc.data() });
          });
          // Find a pending personnel with matching email and password
          const match = pendingList.find(
            (val) => val.email === trimmedEmail && val.password === password && val.status === "pending"
          );
          if (match) {
            // Redirect to the correct signup page based on type
            let signupRoute = "/sign-up";
            // Pass email and type via navigation state
            navigate(signupRoute, { state: { pendingEmail: trimmedEmail, pendingType: match.type, pendingKey: match.id } });
            return;
          } else {
            // If found but doesn't match credentials
            setShowSnackbar(true)
            const idLabel = /^09\d{9}$/.test(trimmedEmail) ? "Mobile number" : "Email address";
            setSnackbarMessage(`${idLabel} does not exist. Please sign up first.`)
            setTimeout(() => setShowSnackbar(false), 5000);
          }
        } else {
          // If not found in system
          setShowSnackbar(true)
          const idLabel = /^09\d{9}$/.test(trimmedEmail) ? "Mobile number" : "Email address";
          if (error?.code === "auth/too-many-requests") {
            setSnackbarMessage("Too many sign-in attempts. Try again later.");
          } else {
            setSnackbarMessage(`${idLabel} does not exist. Please sign up first.`);
          }
          setTimeout(() => setShowSnackbar(false), 5000);
        }
      } catch (pendingError) {
        // Map errors to clearer messages
        const idLbl = /^09\\d{9}$/.test(trimmedEmail) ? "Mobile number" : "Email address";
        setShowSnackbar(true);
        if (pendingError?.code === "auth/too-many-requests") {
          setSnackbarMessage("Too many sign-in attempts. Try again later.");
        } else if (
          pendingError?.code === "auth/invalid-credential" ||
          pendingError?.code === "auth/wrong-password"
        ) {
          setSnackbarMessage(
            `Incorrect ${idLbl.toLowerCase()} or password. Please try again.`
          );
        } else {
          setSnackbarMessage(`${idLbl} does not exist. Please sign up first.`);
        }
        setTimeout(() => setShowSnackbar(false), 5000);
      }
    } finally {
      setIsSigningIn(false);
    }
  };

  // Handle Enter key to submit sign-in
  const handleKeyDown = (e) => {
    if (e.key === "Enter") {
      e.preventDefault();
      // Prevent double submission when already signing in
      if (!isSigningIn) handleSignIn();
    }
  };

  useEffect(() => {
    const unsub = onAuthStateChanged(auth, (u) => {
      // if there's a pending 2FA, allow user to complete it
      if (sessionStorage.getItem("pending2FA")) return;
      // if 2FA was just verified and navigation is in progress, don't interfere
      if (sessionStorage.getItem("2FAJustVerified") === "true") return;
      // if we're closing the 2FA modal, don't navigate
      if (sessionStorage.getItem("closing2FA") === "true") return;
      // if we're explicitly redirecting to verify-email, suppress dashboard redirect
      if (sessionStorage.getItem('suppressPostSignInRedirect') === 'true') return;
      // don't auto-redirect while an explicit sign-in is in progress
      if (isSigningIn) return;
      if (u) {
        // already signed in — always redirect to dashboard (preserving query params)
        navigateToDashboard();
      }
    });
    return () => unsub && unsub();
  }, [auth, navigate, isSigningIn, location]);

  // Post-sign-in safety redirect in case the auth listener fired while isSigningIn was true
  useEffect(() => {
    if (!isSigningIn) {
      if (sessionStorage.getItem("pending2FA")) return;
      if (sessionStorage.getItem("2FAJustVerified") === "true") return;
      if (sessionStorage.getItem("closing2FA") === "true") return;
      if (sessionStorage.getItem('suppressPostSignInRedirect') === 'true') return;
      const u = auth.currentUser;
      if (u) {
        // Always redirect to dashboard (preserving query params)
        navigateToDashboard();
      }
    }
  }, [isSigningIn, auth, navigate, location]);

  // Handle 2FA verification success
  const handle2FASuccess = async () => {
    setShow2FAModal(false);
    
    // Store role before clearing state
    const userRole = pendingUserRole;
    setUserRole(userRole);
    
    // Clear all session flags that might interfere with navigation
    sessionStorage.removeItem("pending2FA");
    sessionStorage.removeItem("mobileSignIn"); // Clear mobile sign-in flag
    sessionStorage.removeItem("suppressPostSignInRedirect"); // Clear suppression flag
    
    // Set flag to indicate 2FA verification just completed
    sessionStorage.setItem("2FAJustVerified", "true");
    
    // Clear pending user data
    setPendingUser(null);
    setPendingUserData(null);
    setPendingUserRole("");
    
    // Show signing in state while waiting for Firestore to propagate
    setIsSigningIn(true);
    
    // Wait for Firestore to propagate before navigating
    await new Promise(resolve => setTimeout(resolve, 1500));
    
    // Navigate to the appropriate dashboard (preserving query params)
    setIsSigningIn(false);
    navigateToDashboard(userRole);
    
    // Clear the flag after navigation completes
    setTimeout(() => {
      sessionStorage.removeItem("2FAJustVerified");
    }, 2000); // Shorter timeout since we already waited
  };

  // Handle 2FA modal close
  const handle2FAClose = (cancelled = false) => {
    // Set flags FIRST to prevent splash screen immediately
    sessionStorage.setItem("closing2FA", "true"); // Persist flag in sessionStorage (set first)
    setIsClosing2FA(true); // Set React state flag
    
    // Clear modal state
    setShow2FAModal(false);
    setPendingUser(null);
    setPendingUserData(null);
    setPendingUserRole("");
    sessionStorage.removeItem("pending2FA");
    sessionStorage.removeItem("mobileSignIn"); // Clear mobile sign-in flag
    sessionStorage.removeItem("suppressPostSignInRedirect"); // Clear suppression flag
    
    // Always sign out when closing 2FA modal - user hasn't completed verification
    // This prevents the user from being stuck on splash screen
    auth.signOut().then(() => {
      setIsClosing2FA(false); // Clear flag after sign-out completes
      sessionStorage.removeItem("closing2FA"); // Clear sessionStorage flag
    }).catch((error) => {
      console.error("Error signing out:", error);
      setIsClosing2FA(false); // Clear flag even on error
      sessionStorage.removeItem("closing2FA"); // Clear sessionStorage flag
    });
  };

  // Show splash only when already authenticated (not during sign-in attempts)
  // But allow 2FA modal to show if 2FA verification is pending
  // Also don't show splash if we're closing the 2FA modal or actively signing in
  const pending2FA = sessionStorage.getItem("pending2FA");
  const closing2FA = sessionStorage.getItem("closing2FA") === "true";
  if (auth.currentUser && !pending2FA && !isClosing2FA && !isSigningIn && !closing2FA) {
    return <SplashScreen isOpen={true} />;
  }

  // Get 2FA methods from user data
  const get2FAMethods = () => {
    if (!pendingUserData || !pendingUserData.twoFactorMethod) {
      return ['email'];
    }
    
    if (pendingUserData.twoFactorMethod.includes(',')) {
      return pendingUserData.twoFactorMethod.split(',').filter(m => m.trim());
    }
    
    return [pendingUserData.twoFactorMethod];
  };

  // Styling
  const buttonStylesTextForgot = {
    color: "#2C251E",
    fontSize: "14px",
    padding: "5px 10px",
    textTransform: "none",
    backgroundColor: "transparent",
  }

  return (
    <div>

      {/* Head Section */}
      <head>
        <title>VSDiamond Dental Clinic - Sign In</title>
      </head>
        
      {/* All Container */}
      <Box
        sx={{
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
          height: "100vh",
          // backgroundColor: "#2e2e2e",
          backgroundColor: "#FDF9F3",
        }}>

        {/* Upper Stars Container */}
        <Box
          sx={{
            display: {
              xs : "none",
              sm : "none",
              md : "flex",
              lg : "flex",
              xl : "flex",
            },
            width: "100%",
            top: 0,
            position: "absolute",
            justifyContent: "end",
            opacity: "0.5",
          }}>

          <Box
            sx={{
              mr: {
                xs: null,
                sm: null,
                md: "20px",
                lg: "35px",
                xl: "35px"
              }
            }}
          >
            <Icon
              src={stars}
              size="stars"/>
          </Box>

        </Box>

        {/* Lower Stars Container */}
        <Box
          sx={{
            display: {
              xs : "none",
              sm : "none",
              md : "flex",
              lg : "flex",
              xl : "flex",
            },
            width: "100%",
            bottom: 0,
            position: "absolute",
            justifyContent: "start",
            opacity: "0.5",
          }}>

          <Box
            sx={{
              ml: {
                xs: null,
                sm: null,
                md: "20px",
                lg: "35px",
                xl: "35px"
              }
            }}
          >
            <Icon
              src={stars}
              size="stars"/>
          </Box>

        </Box>

        {/* Content Container */}
        <Box
          sx={{
            width: "100%",
            maxWidth: {
              xs : "100%",
              sm : "100%",
              md : "700px",
              lg : "700px",
              xl : "700px",
            },
            maxHeight: {
              xs : "100%",
              sm : "100%",
              md : "700px",
              lg : "700px",
              xl : "700px",
            },
            height: "100%",
            margin: {
              xs : null,
              sm : null,
              md : "0 20px",
              lg : "0 20px",
              xl : "0 20px",
            },
            padding: {
              xs : null,
              sm : null,
              md : "15px",
              lg : "15px",
              xl : "15px",
            },
            backgroundColor: "#FEFEFE",
            boxShadow: "0 0 1px 0.1px #2E2D2D",
            borderRadius: 3,
            zIndex: "1",
          }}>

          {/* All Wrapper */}
          <Stack
            direction="column"
            sx={{
              height: "100%",
              justifyContent: "space-between"
            }}>

            {/* Header Container */}
            <Box
              sx={{
                display: "flex",
                margin: {
                  xs : "20px",
                  sm : "20px",
                  md : null,
                  lg : null,
                  xl : null,
                },
                // border: "1px solid black",
              }}>

                {/* Link to Home Page*/}
                <Link 
                  to="/"
                  className="links"
                  style={{ 
                    textDecoration: "none" 
                  }}>
                  
                  {/* Link to Home Page Container */}
                  <Box
                    sx={{
                      display: "flex",
                      flexDirection: "row",
                      justifyContent: "start",
                      alignItems: "center",
                      textDecoration: "none",
                      padding: "10px",
                      cursor: "pointer",
                      "&:hover": {
                        opacity: 0.6,
                        borderRadius: 2,
                        transition: "background-color 0.3s",
                      },
                    }}>

                    {/* Image */}
                    <img
                      src={back}
                      alt="home"
                      className="back-text"
                      style={{
                        transition: "filter 0.2s, width 0.2s, height 0.2s"
                      }}/>

                    {/* Text */}
                    <Typography
                      variant="body1"
                      className="home-text"
                      sx={{
                        color: "#2C251E",
                        fontWeight: "300",
                        fontSize: "14px",
                        padding: "0px 5px",
                        transition: "font-size 0.2s",
                        // border: "1px solid black"
                      }}>Home
                    </Typography>

                  </Box>

                </Link>

            </Box>


            {/* Body Container */}
            <Box
              sx={{
                textAlign: "center",
                justifyItems: "center",
                padding: {
                  xs : "15px",
                  sm : "15px",
                  md : null,
                  lg : null,
                  xl : null,
                },
                // border: "1px solid black"
                alignContent: "center",
              }}>
        
              {/* Logo Container */}
              <Box>
                <img src={logo} alt="logo" className="logos"/>
              </Box>

              {/* Heading Message */}
              <Typography 
                variant="h2"
                sx={{
                  color: "#2C251E",
                  fontSize: {
                    xs: "23px",
                    sm: "30px",
                    md: "30px",
                    lg: "30px",
                    xl: "30px",
                  },
                  padding: "5px 25px",
                }}>Welcome to VSDiamond
              </Typography>

              {/* Subheading Message */}
              {/* <Typography  
                variant="h3"
                sx={{
                  color: "#59544A",
                  fontWeight: "300",
                  fontSize: "17px",
                  padding: "5px 15px",
                }}>Enter your details to sign in.
              </Typography> */}

              {/* Forms Container */}
              <Stack
                spacing={4}
                sx={{
                  // backgroundColor: "aqua",
                  width: {
                    xs: "90%",
                    sm: "50%",
                    md: "50%",
                    lg: "50%",
                    xl: "50%",
                  },
                  textAlign: "center",
                  marginTop: "20px",
                  marginLeft: "auto",
                  marginRight: "auto",
                }}>
        
                {/* {showTutorial && rect && (
                  <TutorialOverlay rect={rect} onClose={() => setShowTutorial(false)} />
                )} */}

                {/* Forms TextBoxes Container */}
                <Stack
                  direction="column"
                  spacing={2}
                  onKeyDown={handleKeyDown}>

                  {/* TextField Email/Mobile Number Container*/}
                  <TextFieldComp
                    placeholder="Enter email/mobile number"
                    id="signin-email"
                    label="Email/Mobile Number"
                    type="text"
                    value={email}
                    color="primary"
                    onChange={(value) => {
                      // Let the user type any format here (email or mobile with spaces, dashes, +63, etc.).
                      // We normalize and strictly validate mobile numbers later in handleSignIn via normalizeMobileNumber.
                      setEmail(value);
                    }}
                    onBlur={() => {
                      // On blur, if the input is a recognizable mobile number in any supported format,
                      // normalize it to strict 09XXXXXXXXX and show that in the field.
                      const normalized = normalizeMobileNumber(email.trim());
                      if (normalized) {
                        setEmail(normalized);
                      }
                    }}
                    state={false}
                    requires={true}
                    check={noInput}>
                  </TextFieldComp >

                  {/* TextField Password Container*/}
                  <TextFieldComp
                    placeholder="********"
                    id="signin-password"
                    label="Password"
                    type={showPassword ? "text" : "password"}
                    value={password}
                    color="primary"
                    showPasswordToggle={true}
                    showPassword={showPassword}
                    onTogglePassword={() => setShowPassword(!showPassword)}
                    onChange={setPassword}
                    state={false}
                    requires={true}
                    check={noInput}>
                  </TextFieldComp>

                </Stack>

                {/* Forgot Password Link */}
                <Box 
                  sx={{
                    textAlign: "end"
                  }}>
                  
                  {/* Link */}
                  <Link
                      to="/forgot-password"
                      className="forgot"
                    >

                  <Button 
                    variant="contained"
                    disableElevation
                    disableRipple
                    className="forgots"
                    sx={buttonStylesTextForgot}>

                    Forgot Password?
                    

                  </Button>
                  </Link>

                </Box>

                {/* Button Container */}
                <Box>

                  {/* Sign In Button */}
                  <Button
                    variant="contained"
                    disabled={isSigningIn}
                    onClick={handleSignIn}
                    sx={{
                      color: "#FDF9F3",
                      backgroundColor: isSigningIn ? "#8F7049" : "#C5A572",
                      fontSize: "14px",
                      borderRadius: "50px",
                      padding: "15px 30px",
                      textTransform: "none",
                      width: "100%",
                      fontWeight: 600,
                      "&:hover": {
                        backgroundColor: isSigningIn ? "#8F7049" : "#8F7049",
                      },
                      "&:disabled": {
                        backgroundColor: "#8F7049",
                        color: "#FDF9F3",
                      },
                    }}
                  >
                    {isSigningIn ? (
                      <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                        <CircularProgress size={20} sx={{ color: "#FDF9F3" }} />
                        <span>Signing In...</span>
                      </Box>
                    ) : (
                      "Sign In"
                    )}
                  </Button>

                </Box>

                {/* Sign Up Link */}
                <Box>

                  {/* Sign Up Link Container */}
                  <Box
                    sx={{
                      display: "flex",
                      flexDirection: {
                        xs: "column",
                        sm: "row",
                        md: "row",
                        lg: "row",
                        xl: "row",
                      },
                      justifyContent: "center",
                      alignItems: "center",
                      textDecoration: "none",
                      "&:hover .back-text": {
                        width: "30px",
                        height: "30px",
                      },
                      "&:hover .home-text": {
                        fontSize: "16px",
                      },
                    }}>
                
                    {/* Text */}
                    <Typography
                      variant="h3"
                        sx={{
                          color: "#2C251E",
                          fontWeight: "300",
                          fontSize: "14px",
                      }}>Don't have an account?
                    </Typography>

                    {/* Button */}
                    <Button
                      variant="contained"
                      disableElevation
                      sx={{
                        color: "#2C251E",
                        fontSize: "14px",
                        padding: "5px 10px",
                        textTransform: "none",
                        backgroundColor: "transparent",
                      }}>
                    
                      {/* Link  */}
                      <Link
                        to="/sign-up"
                        className="link"
                      >Sign Up
                      </Link>
                      
                    </Button>

                  </Box>

                </Box>

              </Stack>

            </Box>
              
            {/* Footer Container */}
            <Box
              sx={{
                display: "flex",
                // border: "1px solid black",
                padding: "5px",
              }}>
            </Box>

          </Stack>
            
        </Box>

      </Box>

      {/* Alert Message */}
      <AlertComp
        message={alertMessage}
        title={alertTitle}
        isOpen={showCustomAlert}
        image={alertImage}
        button={alertButton}
        buttonType={alertButtonType}
        destination={alertLink}
        onClose={() => setShowCustomAlert(false)}/>

      {/* Two-Factor Authentication Modal */}
      {show2FAModal && pendingUser && pendingUserData && (
        <TwoFactorModal
          isOpen={show2FAModal}
          onClose={handle2FAClose}
          onSuccess={handle2FASuccess}
          userId={pendingUser.uid}
          userDetails={pendingUserData}
          methods={get2FAMethods()}
          requireInitialSend={true}
        />
      )}

      <Snackbar
        open={showSnackbar}
        message={snackbarMessage}
        background="error"
        autoHideDuration={5000}
        onClose={() => setShowSnackbar(false)}
      />

      <SplashScreen
        message={alertMessage}
        title={alertTitle}
        isOpen={showSplash}
        image={alertImage}
        button={alertButton}
        buttonType={alertButtonType}
        destination={alertLink}
        onClose={() => setShowCustomAlert(false)}/>

    </div>
  );
};

export default SignIn;